import React, { useState } from 'react';
import styled from '@emotion/styled/macro';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isAndroid } from 'react-device-detect';
import IconButton from '@mui/material/IconButton';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { authSelectors } from 'redux/auth';
import useBrand from 'hooks/useBrand';
import appDownloadPrompt from 'services/appDownloadPrompt';
import GooglePlayBadge from './google-play-badge.svg';
import AppStoreBadge from './app-store-badge.svg';

// The card's own width decides the layout, since the sidebar leaves it much narrower than the screen
const Container = styled('div')`
  container-type: inline-size;
`;

const Card = styled('section', { shouldForwardProp: prop => !['background', 'withImage'].includes(prop) })`
  position: relative;
  overflow: hidden;

  display: grid;
  grid-template-columns: 150px minmax(0, 1fr);
  grid-template-areas:
    'image title'
    'image reason'
    'image badges';
  grid-template-rows: auto auto 1fr;
  column-gap: 1.25rem;
  padding: 1.25rem 3rem 1.25rem 1.25rem;

  background: ${({ background }) => background};
  color: #fff;
  border-radius: 4px;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.05), 0px 2px 6px rgba(0, 0, 0, 0.05);

  @container (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr) 90px;
    grid-template-areas:
      'title title'
      'reason reason'
      'badges image';
    grid-template-rows: auto;
    padding-right: 1.25rem;
  }

  ${({ withImage }) => !withImage && `
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'title' 'reason' 'badges';
  `}
`;

const DeviceImage = styled('img')`
  grid-area: image;
  align-self: end;

  width: 100%;
  margin-bottom: -1.25rem;
`;

const Title = styled('h3')`
  grid-area: title;

  margin: 0 0 0.5rem;
  padding-right: 2rem;

  font-size: 1.2rem;
  font-weight: 700;
`;

const Reason = styled('p')`
  grid-area: reason;

  margin: 0;
  max-width: 60ch;
  line-height: 1.5;
`;

const Badges = styled('div')`
  grid-area: badges;
  align-self: start;

  margin-top: 0.875rem;

  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;

  img {
    display: block;
    height: 40px;
  }
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;

  color: #fff;
`;

const AppDownloadWidget = (args) => {

  const { t, i18n } = useTranslation(['component']);

  const workspace = useSelector(authSelectors.getWorkspace);
  const brand = useBrand();

  const [shown, setShown] = useState(appDownloadPrompt.isShown);

  if (!shown || !brand.apps) {
    return null;
  }

  const hide = () => {
    appDownloadPrompt.hide();
    setShown(false);
  };

  const deviceImage = brand.appDownloadImages?.[i18n.language.match(/^et.*/) ? 'et' : 'en'];

  const badges = [
    <a key="app-store" href={brand.apps.appStore}><img src={AppStoreBadge} alt={t('welcome.appStore')} /></a>,
    <a key="play-store" href={brand.apps.googlePlay}><img src={GooglePlayBadge} alt={t('welcome.googlePlay')} /></a>,
  ];

  return (
    <Container {...args}>
      <Card background={brand.appDownloadColor} withImage={!!deviceImage} aria-label={t('welcome.downloadAppTitle')}>
        <CloseButton size="small" aria-label={t('welcome.downloadAppClose')} title={t('welcome.downloadAppClose')} onClick={hide}>
          <CloseRoundedIcon fontSize="small" />
        </CloseButton>
        {deviceImage && <DeviceImage src={deviceImage} alt="" />}
        <Title>{t('welcome.downloadAppTitle')}</Title>
        <Reason>{t('welcome.downloadAppReason', { workspaceName: workspace.name })}</Reason>
        <Badges>{isAndroid ? badges.reverse() : badges}</Badges>
      </Card>
    </Container>
  );
};

export default AppDownloadWidget;
